/**
 * Turning every valid start into a few worth looking at.
 *
 * `/free-slots` returns every position a task could take, stepping every half hour. That is
 * the right answer for an API — an assistant asking "when could this go" wants the full set —
 * but it is the wrong thing to draw. An empty day and a one-hour task produce about twenty
 * options, each an hour long and each overlapping the next, which renders as a solid wall of
 * bands with no distinguishable choices in it. The first version of this feature did exactly
 * that, and it was unusable in both directions: a person could not pick from it, and neither
 * could a test.
 *
 * So the thinning is a presentation decision and it lives here rather than on the server: the
 * API keeps telling the whole truth and the screen offers a handful of genuinely different
 * times.
 */

export interface Slot {
  start: string;
  end: string;
  preferred: boolean;
  /** 0 is a first-choice window, 1 a second, `null` outside them all. */
  preferenceRank?: number | null;
}

/** How many to offer. Four is enough to feel like a choice and few enough to read. */
export const MAX_CHOICES = 4;

const startMs = (slot: Slot): number => new Date(slot.start).getTime();

/**
 * A few non-overlapping options, spread across the day.
 *
 * The earliest is always offered, because "the next free hour" is the most common thing
 * anyone wants. The rest are spread over the remaining span instead of taken consecutively:
 * 06:00, 07:00, 08:00, 09:00 are four ways of saying "this morning", where 06:00, 10:30,
 * 14:00, 18:30 are four actual alternatives.
 *
 * A slot inside a template's preferred window wins its bucket, since that is the whole point
 * of recording a preferred window.
 */
export function distinctSlots<T extends Slot>(
  slots: readonly T[],
  durationMinutes: number,
  limit = MAX_CHOICES,
): T[] {
  if (slots.length === 0 || limit <= 0) return [];

  const ordered = [...slots].sort((a, b) => startMs(a) - startMs(b));
  const first = ordered[0]!;
  if (limit === 1 || ordered.length === 1) return [first];

  const durationMs = Math.max(1, durationMinutes) * 60_000;
  // Nothing may overlap anything already chosen, or two "different" options would be the
  // same hour wearing different labels.
  const clear = (slot: Slot, chosen: Slot[]): boolean =>
    chosen.every((c) => {
      const gap = Math.abs(startMs(slot) - startMs(c));
      return gap >= durationMs;
    });

  const remaining = ordered.slice(1).filter((s) => clear(s, [first]));
  if (remaining.length === 0) return [first];

  const chosen: T[] = [first];
  const span = startMs(remaining[remaining.length - 1]!) - startMs(remaining[0]!);
  const buckets = Math.min(limit - 1, remaining.length);

  for (let i = 0; i < buckets; i += 1) {
    // Aim at an even spread, then take the nearest candidate that does not collide with
    // anything already chosen. Aiming rather than slicing keeps the spread even when the
    // day is lopsided — a free morning and a booked afternoon should still offer both.
    //
    // The targets start one bucket in rather than at the beginning of the span. Aiming at
    // the span's start meant the second option was always the slot immediately after the
    // first — 05:00 and 05:30 on the running deployment — which spends a choice on a time
    // the user already has.
    const target = startMs(remaining[0]!) + (span * (i + 1)) / buckets;
    const candidates = remaining
      .filter((s) => clear(s, chosen))
      .sort((a, b) => Math.abs(startMs(a) - target) - Math.abs(startMs(b) - target));
    if (candidates.length === 0) break;

    /*
      A preferred slot near the target wins.

      This used to require the distances to be *exactly* equal, which on a half-hourly grid
      essentially never happened — so "usually in the morning" was recorded and then thinned
      away before it reached the screen. Near enough is within one slot of the task's own
      length: close enough that the spread still reads as a spread, loose enough to actually
      fire.
    */
    const closest = Math.abs(startMs(candidates[0]!) - target);
    const best =
      candidates.find(
        (c) => c.preferred && Math.abs(startMs(c) - target) - closest <= durationMs,
      ) ?? candidates[0]!;
    chosen.push(best);
  }

  /*
    If the template has a preferred window, at least one option must be inside it.

    An even spread can still miss the window entirely — a preferred 07:00–09:00 on a day whose
    buckets land at 06:00, 11:00 and 16:00 offers nothing the template asked for. The earliest
    choice is kept whatever happens, because "the next free hour" is still the most common want;
    the swap replaces the least useful of the rest.
  */
  const preferred = ordered.filter((s) => s.preferred);
  if (preferred.length > 0 && !chosen.some((s) => s.preferred) && chosen.length > 1) {
    const candidate = preferred.find((s) => clear(s, [first]));
    if (candidate) chosen[chosen.length - 1] = candidate;
  }

  return chosen.sort((a, b) => startMs(a) - startMs(b)).slice(0, limit);
}
